import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ROUTES from '../routes.json';
import PageHelmet from '../components/PageHelmet';
import { normalizeRoute } from '../utils/routeHelpers';


function AiTagGenerator() {
  // Head handled via PageHelmet
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^['"]+|['"]+$/g, '').trim().replace(/\/+$/,'');
  const normalizeResponse = (data) => {
    if (typeof data === 'object') return data;
    try { return JSON.parse(data); } catch { return { success: true, response: data }; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!prompt.trim()) { setError('Please enter a blog title.'); return; }
    setLoading(true);
    try {
      let response;
      try {
        // Hardcoded backend API URL

        response = await axios.post(`${API_URL}/api/tags/generate`, { prompt });
      } catch (err) {
        if (err.response) {
          setError(`Server ${err.response.status}: ${JSON.stringify(err.response.data)}`);
        } else {
          setError(err.message);
        }
        setLoading(false);
        return;
      }
      const normalized = normalizeResponse(response.data);
      setResult(normalized);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;
    if (result.success === false) return <pre>{JSON.stringify(result, null, 2)}</pre>;
    const resp = result.response ?? result;

    // Preferred structured object with exact fields
    if (resp && typeof resp === 'object' && (resp.metaTitle || resp.metaDescription || resp.slug || resp.tags)) {
      return (
        <div>
          {resp.metaTitle && (
            <div style={{marginBottom:12}}>
              <h3 style={{marginBottom:6}}>Meta Title</h3>
              <div className="hero-caption">{resp.metaTitle}</div>
            </div>
          )}

          {resp.metaDescription && (
            <div style={{marginBottom:12}}>
              <h3 style={{marginBottom:6}}>Meta Description</h3>
              <div className="hero-caption" style={{whiteSpace:'pre-wrap'}}>{resp.metaDescription}</div>
            </div>
          )}

          {resp.slug && (
            <div style={{marginBottom:12}}>
              <h3 style={{marginBottom:6}}>Slug</h3>
              <div className="hero-caption">{resp.slug}</div>
            </div>
          )}

          {resp.tags && (
            <div style={{marginBottom:12}}>
              <h3 style={{marginBottom:6}}>Tags</h3>
              {Array.isArray(resp.tags) ? (
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {resp.tags.map((t, idx) => (
                    <span key={idx} className="list-link" style={{padding:'6px 8px',borderRadius:8,background:'#fbfdff',border:'1px solid #eef2ff',fontSize:14}}>{t}</span>
                  ))}
                </div>
              ) : (
                <div className="hero-caption">{String(resp.tags)}</div>
              )}
            </div>
          )}
        </div>
      );
    }

    // If response is plain text or other shape, show JSON/raw
  if (typeof resp === 'string') return <div className="hero-caption" style={{whiteSpace:'pre-wrap'}}>{resp}</div>;
  return <div className="hero-caption" style={{whiteSpace:'pre-wrap'}}>{JSON.stringify(resp, null, 2)}</div>;
  };

  return (
    <div className="app-root">
      <PageHelmet
        title="Meta Tag Generator — SEO Titles & Tags | OneAIHuman"
        description="Generate optimized meta titles, descriptions, slugs and tag lists from a draft headline to improve search discoverability and social previews."
        canonical={window.location.origin + normalizeRoute(ROUTES.tagGenerator.path)}
      />
      <div className="app-card">
        <div className="header-sub">
          <h1 className="page-title">Meta Tag Generator</h1>
          <p className="page-sub">Generate SEO-friendly meta title, description, slug and tags from a post title.</p>
        </div>

        <div className="page-content" style={{ marginTop: 12, color: '#6b7280' }}>
          <p>
            The Meta Tag Generator helps authors and editors produce optimized meta titles, descriptions, and slugs quickly from a draft headline. Good metadata improves discoverability in search engines and helps social platforms display useful excerpts. This tool recommends concise titles and descriptions that balance keywords and readability.
          </p>
          <p>
            Designed to fit into editorial workflows, the generator provides structured outputs you can copy directly into your CMS. It also suggests slugs and tag lists that reflect the post's focus and audience intent.
          </p>
          <ul style={{ marginTop: 8 }}>
            <li>SEO-friendly title and description suggestions.</li>
            <li>Suggested slugs and tag lists for consistent publishing.</li>
            <li>Designed for quick copy-paste into CMS fields.</li>
          </ul>
        </div>

        <div className="main">
          <div className="tool-area">
            <div className="panel panel-left">
              <form onSubmit={handleSubmit} style={{width:'100%'}}>
                <input
                  type="text"
                  className="input-area"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter blog title..."
                />

                <div style={{marginTop:12,display:'flex',gap:10}}>
                  <button className="btn primary" type="submit" disabled={loading}>
                    {loading ? 'Generating...' : 'Generate'}
                  </button>
                  <button type="button" className="btn ghost" onClick={() => { setPrompt(''); setResult(null); setError(null); }}>
                    Clear
                  </button>
                </div>
              </form>

              {error && <div style={{ color: 'crimson', marginTop:12 }}><strong>Error:</strong> {String(error)}</div>}
            </div>

            <div className="panel">
              <h2 style={{marginTop:0}}>Result</h2>
              <div style={{marginTop:8}}>
                {!error && !result && <div className="hero-caption">No result yet</div>}
                {renderResult()}
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Internal outgoing links (SPA) */}
        <div style={{ marginTop: 20, padding: 8, textAlign: 'center' }}>
          <h4>Explore more</h4>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={normalizeRoute(ROUTES.humanizer.path)} className="btn ghost">Try the Humanizer</Link>
            <Link to={normalizeRoute(ROUTES.blog.path)} className="btn ghost">Visit our Blog</Link>
            <Link to={normalizeRoute(ROUTES.contact.path)} className="btn ghost">Contact Us</Link>
          </div>
        </div>
      </div>
    );
  }

  export default AiTagGenerator;
